<template>
  <div class="budget-programming-tracking">
    <!-- 1. KPI SUMMARY STRIP -->
    <v-row dense class="mb-4">
      <!-- Presupuesto Maestro Total -->
      <v-col cols="12" sm="6" md="3">
        <v-card class="pa-3 rounded-xl border-light bg-white" outlined elevation="0">
          <div class="d-flex align-center">
            <v-avatar color="blue lighten-5" size="42" class="mr-3">
              <v-icon color="blue darken-3">mdi-chart-box-outline</v-icon>
            </v-avatar>
            <div>
              <div class="caption font-weight-bold grey--text text--darken-2 text-uppercase">
                Presupuesto Meta
              </div>
              <div class="text-h6 font-weight-black grey--text text--darken-4">
                {{ totals.BudgetTons | formatNumber }} TM
              </div>
              <div class="caption font-weight-bold grey--text text--darken-2">
                {{ totals.BudgetFcl | formatFcl }} FCL • ${{ totals.BudgetFobUsd | formatMoney }}
              </div>
            </div>
          </div>
        </v-card>
      </v-col>

      <!-- Planificado (Open Volume) -->
      <v-col cols="12" sm="6" md="3">
        <v-card class="pa-3 rounded-xl border-light bg-white" outlined elevation="0">
          <div class="d-flex align-center">
            <v-avatar color="amber lighten-5" size="42" class="mr-3">
              <v-icon color="amber darken-4">mdi-clock-outline</v-icon>
            </v-avatar>
            <div>
              <div class="caption font-weight-bold grey--text text--darken-2 text-uppercase">
                Planificado (Open Volume)
              </div>
              <div class="text-h6 font-weight-black amber--text text--darken-4">
                {{ totals.PlannedTons | formatNumber }} TM
              </div>
              <div class="caption font-weight-bold grey--text text--darken-2">
                {{ totals.PlannedFcl | formatFcl }} FCL • ${{ totals.PlannedFobUsd | formatMoney }}
              </div>
            </div>
          </div>
        </v-card>
      </v-col>

      <!-- Vendido con Contrato -->
      <v-col cols="12" sm="6" md="3">
        <v-card class="pa-3 rounded-xl border-light bg-white" outlined elevation="0">
          <div class="d-flex align-center">
            <v-avatar color="green lighten-5" size="42" class="mr-3">
              <v-icon color="green darken-3">mdi-file-certificate-outline</v-icon>
            </v-avatar>
            <div>
              <div class="caption font-weight-bold grey--text text--darken-2 text-uppercase">
                Vendido con Contrato
              </div>
              <div class="text-h6 font-weight-black green--text text--darken-3">
                {{ totals.SoldTons | formatNumber }} TM
              </div>
              <div class="caption font-weight-bold grey--text text--darken-2">
                {{ totals.SoldFcl | formatFcl }} FCL • ${{ totals.SoldFobUsd | formatMoney }}
              </div>
            </div>
          </div>
        </v-card>
      </v-col>

      <!-- Total Programado & Variación Separada -->
      <v-col cols="12" sm="6" md="3">
        <v-card class="pa-3 rounded-xl border-light bg-white" outlined elevation="0">
          <div class="d-flex align-center">
            <v-avatar :color="varianceBadgeColor.bg" size="42" class="mr-3">
              <v-icon :color="varianceBadgeColor.icon">{{ varianceBadgeColor.iconName }}</v-icon>
            </v-avatar>
            <div>
              <div class="caption font-weight-bold grey--text text--darken-2 text-uppercase">
                Total Programado
              </div>
              <div class="text-h6 font-weight-black grey--text text--darken-4">
                {{ totals.TotalProgrammedTons | formatNumber }} TM
              </div>
              <div class="caption font-weight-bold" :class="varianceTextColor">
                Avance: {{ budgetAdvancePercent }}% | Var: {{ totals.VarianceTons > 0 ? '+' : '' }}{{ totals.VarianceTons | formatNumber }} TM
              </div>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- 2. TOOLBAR: SEARCH, FILTERS & EXPORT -->
    <v-card class="pa-3 mb-4 rounded-xl border-light bg-white" outlined elevation="0">
      <v-row dense align="center" justify="space-between">
        <v-col cols="12" sm="4" md="4">
          <s-text
            v-model="search"
            placeholder="Buscar por cliente o código SAP..."
            dense
            hide-details
            clearable
          />
        </v-col>

        <v-col cols="12" sm="4" md="3">
          <s-select
            v-model="filterStatus"
            :items="filterStatusOptions"
            label="Filtro de Situación"
            dense
            hide-details
            clearable
          />
        </v-col>

        <v-col cols="12" sm="4" md="5" class="d-flex align-center justify-end flex-wrap">
          <span v-if="lastUpdateDate" class="caption grey--text text--darken-2 mr-3">
            <v-icon x-small color="grey darken-2">mdi-history</v-icon>
            Últ. Actualización: {{ formatDateTime(lastUpdateDate) }}
          </span>

          <v-btn
            text
            small
            black
            class="rounded-lg font-weight-bold text-none mr-2"
            @click="loadTrackingData"
            :loading="loading"
          >
            <v-icon style="font-size:16px;" class="mr-1">mdi-refresh</v-icon>
            Refrescar
          </v-btn>

          <v-btn
            color="primary"
            small
            class="rounded-lg font-weight-bold text-none"
            elevation="0"
            @click="exportToExcel"
            :disabled="filteredList.length === 0"
          >
            <v-icon style="font-size:16px;" class="mr-1">mdi-file-excel</v-icon>
            Exportar
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- 3. CONSOLIDATED TRACKING TABLE (2-LEVEL HEADERS + STICKY CLIENT COLUMN) -->
    <v-card class="rounded-xl border-light bg-white pa-0 mb-2 overflow-hidden" outlined elevation="0">
      <v-skeleton-loader v-if="loading" type="table" />

      <div v-else-if="filteredList.length === 0" class="text-center py-10">
        <v-icon size="56" color="grey lighten-1">mdi-account-search-outline</v-icon>
        <div class="text-subtitle-1 font-weight-bold grey--text text--darken-2 mt-2">
          No se encontraron registros en el consolidado
        </div>
        <div class="caption grey--text text--darken-1">
          No hay clientes que coincidan con el criterio de búsqueda o filtro seleccionado.
        </div>
      </div>

      <div v-else class="table-container">
        <v-data-table
          :headers="headers"
          :items="filteredList"
          dense
          hide-default-header
          hide-default-footer
          :items-per-page="-1"
          class="border-0 tracking-table"
        >
          <!-- ENCABEZADO AGRUPADO DE DOS NIVELES -->
          <template v-slot:header>
            <thead>
              <!-- Nivel 1: Super Headers -->
              <tr class="super-headers">
                <th rowspan="2" class="text-left sticky-col border-right super-header-client">
                  Cliente Comercial (SAP)
                </th>
                <th colspan="3" class="text-center super-header-group border-right">
                  Presupuesto
                </th>
                <th colspan="3" class="text-center super-header-group border-right">
                  Planificado (Open Volume)
                </th>
                <th colspan="3" class="text-center super-header-group border-right">
                  Vendido con Contrato
                </th>
                <th colspan="3" class="text-center super-header-group border-right">
                  Total Programado
                </th>
                <th colspan="3" class="text-center super-header-group border-right">
                  Variación
                </th>
                <th rowspan="2" class="text-center super-header-action">
                  Líneas
                </th>
              </tr>

              <!-- Nivel 2: Sub Headers -->
              <tr class="sub-headers">
                <!-- Presupuesto -->
                <th class="text-right">TM</th>
                <th class="text-right">FCL</th>
                <th class="text-right border-right">FOB USD</th>

                <!-- Planificado -->
                <th class="text-right">TM</th>
                <th class="text-right">FCL</th>
                <th class="text-right border-right">FOB USD</th>

                <!-- Vendido -->
                <th class="text-right">TM</th>
                <th class="text-right">FCL</th>
                <th class="text-right border-right">FOB USD</th>

                <!-- Total Programado -->
                <th class="text-right">TM</th>
                <th class="text-right">FCL</th>
                <th class="text-right border-right">FOB USD</th>

                <!-- Variación -->
                <th class="text-center">TM</th>
                <th class="text-center">FCL</th>
                <th class="text-right border-right">FOB USD</th>
              </tr>
            </thead>
          </template>

          <!-- CLIENTE (SAP + Nombre + chip) - COLUMNA FIJA STICKY -->
          <template v-slot:[`item.CustomerName`]="{ item }">
            <div class="d-flex align-center py-1 cursor-pointer" @click="openCustomerDrawer(item)">
              <v-avatar
                size="30"
                :color="item.IsNoClient ? 'amber lighten-4' : 'blue lighten-5'"
                class="mr-2 flex-shrink-0"
              >
                <v-icon small :color="item.IsNoClient ? 'amber darken-4' : 'primary'">
                  {{ item.IsNoClient ? 'mdi-account-question-outline' : 'mdi-account-tie' }}
                </v-icon>
              </v-avatar>
              <div>
                <div class="d-flex align-center flex-wrap">
                  <span class="font-weight-black grey--text text--darken-4 text-subtitle-2 hover-primary">
                    {{ item.CustomerName }}
                  </span>
                  <v-chip
                    v-if="item.IsNoClient"
                    x-small
                    label
                    color="amber darken-3"
                    text-color="white"
                    class="font-weight-bold ml-1"
                  >
                    Sin Cliente
                  </v-chip>
                  <v-chip
                    v-else-if="Number(item.AllocatedFromBagTons || 0) > 0 || Number(item.AllocatedFromBagFcl || 0) > 0"
                    x-small
                    label
                    color="deep-purple lighten-4"
                    text-color="deep-purple darken-4"
                    class="font-weight-bold ml-1"
                  >
                    Asignado desde bolsa
                  </v-chip>
                  <v-chip
                    v-else-if="item.BudgetTons === 0 && item.TotalProgrammedTons > 0"
                    x-small
                    label
                    outlined
                    color="purple"
                    class="font-weight-bold ml-1"
                  >
                    No Presupuestado
                  </v-chip>
                </div>
                <div v-if="item.PrsCustomerID" class="caption grey--text text--darken-2">
                  SAP: <code>{{ item.PrsCustomerID }}</code>
                </div>
              </div>
            </div>
          </template>

          <!-- PRESUPUESTO: TM -->
          <template v-slot:[`item.BudgetTons`]="{ item }">
            <span class="font-weight-bold body-2 grey--text text--darken-4">
              {{ item.BudgetTons | formatNumber }}
            </span>
          </template>

          <!-- PRESUPUESTO: FCL -->
          <template v-slot:[`item.BudgetFcl`]="{ item }">
            <span class="body-2 grey--text text--darken-3">
              {{ item.BudgetFcl | formatFcl }}
            </span>
          </template>

          <!-- PRESUPUESTO: FOB USD -->
          <template v-slot:[`item.BudgetFobUsd`]="{ item }">
            <span class="body-2 grey--text text--darken-3">
              ${{ item.BudgetFobUsd | formatMoney }}
            </span>
          </template>

          <!-- PLANIFICADO: TM -->
          <template v-slot:[`item.PlannedTons`]="{ item }">
            <span class="font-weight-bold body-2 amber--text text--darken-4">
              {{ item.PlannedTons | formatNumber }}
            </span>
          </template>

          <!-- PLANIFICADO: FCL -->
          <template v-slot:[`item.PlannedFcl`]="{ item }">
            <span class="body-2 grey--text text--darken-3">
              {{ item.PlannedFcl | formatFcl }}
            </span>
          </template>

          <!-- PLANIFICADO: FOB USD -->
          <template v-slot:[`item.PlannedFobUsd`]="{ item }">
            <span class="body-2 grey--text text--darken-3">
              ${{ item.PlannedFobUsd | formatMoney }}
            </span>
          </template>

          <!-- VENDIDO: TM -->
          <template v-slot:[`item.SoldTons`]="{ item }">
            <span class="font-weight-bold body-2 green--text text--darken-3">
              {{ item.SoldTons | formatNumber }}
            </span>
          </template>

          <!-- VENDIDO: FCL -->
          <template v-slot:[`item.SoldFcl`]="{ item }">
            <span class="body-2 grey--text text--darken-3">
              {{ item.SoldFcl | formatFcl }}
            </span>
          </template>

          <!-- VENDIDO: FOB USD -->
          <template v-slot:[`item.SoldFobUsd`]="{ item }">
            <span class="body-2 grey--text text--darken-3">
              ${{ item.SoldFobUsd | formatMoney }}
            </span>
          </template>

          <!-- TOTAL PROGRAMADO: TM -->
          <template v-slot:[`item.TotalProgrammedTons`]="{ item }">
            <span class="font-weight-black body-2 grey--text text--darken-4">
              {{ item.TotalProgrammedTons | formatNumber }}
            </span>
          </template>

          <!-- TOTAL PROGRAMADO: FCL -->
          <template v-slot:[`item.TotalProgrammedFcl`]="{ item }">
            <span class="font-weight-bold body-2 grey--text text--darken-3">
              {{ item.TotalProgrammedFcl | formatFcl }}
            </span>
          </template>

          <!-- TOTAL PROGRAMADO: FOB USD -->
          <template v-slot:[`item.TotalProgrammedFobUsd`]="{ item }">
            <span class="font-weight-bold body-2 blue--text text--darken-3">
              ${{ item.TotalProgrammedFobUsd | formatMoney }}
            </span>
          </template>

          <!-- VARIACIÓN TM (Naranja: Pendiente, Verde: Cubierto, Rojo: Exceso, Morado: Bolsa) -->
          <template v-slot:[`item.VarianceTons`]="{ item }">
            <v-chip
              x-small
              class="font-weight-bold"
              :color="getVarianceColor(item.VarianceTons, item).bg"
              :text-color="getVarianceColor(item.VarianceTons, item).text"
            >
              <v-icon x-small class="mr-1">{{ getVarianceColor(item.VarianceTons, item).icon }}</v-icon>
              {{ item.VarianceTons > 0 ? '+' : '' }}{{ item.VarianceTons | formatNumber }} TM
            </v-chip>
          </template>

          <!-- VARIACIÓN FCL (Naranja: Pendiente, Verde: Cubierto, Rojo: Exceso, Morado: Bolsa) -->
          <template v-slot:[`item.VarianceFcl`]="{ item }">
            <v-chip
              x-small
              class="font-weight-bold"
              :color="getVarianceColor(item.VarianceFcl, item).bg"
              :text-color="getVarianceColor(item.VarianceFcl, item).text"
            >
              <v-icon x-small class="mr-1">{{ getVarianceColor(item.VarianceFcl, item).icon }}</v-icon>
              {{ item.VarianceFcl > 0 ? '+' : '' }}{{ item.VarianceFcl | formatFcl }} FCL
            </v-chip>
          </template>

          <!-- VARIACIÓN FOB USD -->
          <template v-slot:[`item.VarianceFobUsd`]="{ item }">
            <span
              class="font-weight-bold body-2"
              :class="getVarianceTextColor(item.VarianceFobUsd, item)"
            >
              {{ item.VarianceFobUsd > 0 ? '+$' : (item.VarianceFobUsd < 0 ? '-$' : '$') }}{{ Math.abs(Number(item.VarianceFobUsd || 0)) | formatMoney }}
            </span>
          </template>

          <!-- ACCIONES: VER DRAWER -->
          <template v-slot:[`item.actions`]="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="primary"
                  v-bind="attrs"
                  v-on="on"
                  @click.stop="openCustomerDrawer(item)"
                >
                  <v-icon small>mdi-chevron-right-circle-outline</v-icon>
                </v-btn>
              </template>
              <span>Ver líneas en detalle ({{ item.TotalLines || 0 }})</span>
            </v-tooltip>
          </template>

          <!-- FILA DE TOTALES GENERALES (FOOTER DE TABLA) -->
          <template v-slot:body.append>
            <tr class="font-weight-black bg-grey-lighten-4 totals-row">
              <td class="text-left py-3 sticky-col border-right">
                <span class="text-subtitle-2 font-weight-black grey--text text--darken-4 text-uppercase">
                  Totales Generales ({{ filteredList.length }} Clientes)
                </span>
              </td>

              <!-- Presupuesto -->
              <td class="text-right font-weight-black grey--text text--darken-4">
                {{ totals.BudgetTons | formatNumber }}
              </td>
              <td class="text-right font-weight-black grey--text text--darken-4">
                {{ totals.BudgetFcl | formatFcl }}
              </td>
              <td class="text-right font-weight-black grey--text text--darken-4 border-right">
                ${{ totals.BudgetFobUsd | formatMoney }}
              </td>

              <!-- Planificado -->
              <td class="text-right font-weight-black amber--text text--darken-4">
                {{ totals.PlannedTons | formatNumber }}
              </td>
              <td class="text-right font-weight-black amber--text text--darken-4">
                {{ totals.PlannedFcl | formatFcl }}
              </td>
              <td class="text-right font-weight-black amber--text text--darken-4 border-right">
                ${{ totals.PlannedFobUsd | formatMoney }}
              </td>

              <!-- Vendido -->
              <td class="text-right font-weight-black green--text text--darken-3">
                {{ totals.SoldTons | formatNumber }}
              </td>
              <td class="text-right font-weight-black green--text text--darken-3">
                {{ totals.SoldFcl | formatFcl }}
              </td>
              <td class="text-right font-weight-black green--text text--darken-3 border-right">
                ${{ totals.SoldFobUsd | formatMoney }}
              </td>

              <!-- Total Programado -->
              <td class="text-right font-weight-black grey--text text--darken-4">
                {{ totals.TotalProgrammedTons | formatNumber }}
              </td>
              <td class="text-right font-weight-black grey--text text--darken-4">
                {{ totals.TotalProgrammedFcl | formatFcl }}
              </td>
              <td class="text-right font-weight-black blue--text text--darken-3 border-right">
                ${{ totals.TotalProgrammedFobUsd | formatMoney }}
              </td>

              <!-- Variación TM, FCL, FOB -->
              <td class="text-center font-weight-black">
                <v-chip
                  small
                  class="font-weight-black"
                  :color="getVarianceColor(totals.VarianceTons).bg"
                  :text-color="getVarianceColor(totals.VarianceTons).text"
                >
                  {{ totals.VarianceTons > 0 ? '+' : '' }}{{ totals.VarianceTons | formatNumber }} TM
                </v-chip>
              </td>
              <td class="text-center font-weight-black">
                <v-chip
                  small
                  class="font-weight-black"
                  :color="getVarianceColor(totals.VarianceFcl).bg"
                  :text-color="getVarianceColor(totals.VarianceFcl).text"
                >
                  {{ totals.VarianceFcl > 0 ? '+' : '' }}{{ totals.VarianceFcl | formatFcl }} FCL
                </v-chip>
              </td>
              <td class="text-right font-weight-black border-right">
                <span :class="getVarianceTextColor(totals.VarianceFobUsd)">
                  {{ totals.VarianceFobUsd > 0 ? '+$' : (totals.VarianceFobUsd < 0 ? '-$' : '$') }}{{ Math.abs(totals.VarianceFobUsd) | formatMoney }}
                </span>
              </td>

              <td class="text-center">-</td>
            </tr>
          </template>
        </v-data-table>
      </div>
    </v-card>

    <!-- 4. DRAWER DE LÍNEAS DETALLADAS DEL CLIENTE -->
    <CustomerProgrammingDrawer
      v-model="drawer.show"
      :emb-id="embId"
      :customer="drawer.customer"
    />
  </div>
</template>

<script>
import CommercialMasterBudgetService from "@/services/ExternalTrade/CommercialMasterBudgetService";
import CustomerProgrammingDrawer from "./CustomerProgrammingDrawer.vue";

export default {
  name: "BudgetProgrammingTracking",
  components: {
    CustomerProgrammingDrawer,
  },
  props: {
    embId: {
      type: Number,
      required: true,
    },
    budgetData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      trackingList: [],
      loading: false,
      search: "",
      filterStatus: null,
      filterStatusOptions: [
        { text: "Todos los Clientes", value: null },
        { text: "Solo con Programación (> 0 TM)", value: "WITH_PROG" },
        { text: "Pendientes por Cubrir (Var < 0)", value: "PENDING" },
        { text: "Cubiertos Exactos (Var = 0)", value: "COVERED" },
        { text: "Exceso de Programación (Var > 0)", value: "EXCEEDED" },
        { text: "Sin Cliente Asignado", value: "NO_CLIENT" },
      ],
      drawer: {
        show: false,
        customer: null,
      },
      headers: [
        { text: "Cliente Comercial (SAP)", value: "CustomerName", align: "left", width: "260px" },
        // Presupuesto
        { text: "TM", value: "BudgetTons", align: "right" },
        { text: "FCL", value: "BudgetFcl", align: "right" },
        { text: "FOB USD", value: "BudgetFobUsd", align: "right" },
        // Planificado
        { text: "TM", value: "PlannedTons", align: "right" },
        { text: "FCL", value: "PlannedFcl", align: "right" },
        { text: "FOB USD", value: "PlannedFobUsd", align: "right" },
        // Vendido
        { text: "TM", value: "SoldTons", align: "right" },
        { text: "FCL", value: "SoldFcl", align: "right" },
        { text: "FOB USD", value: "SoldFobUsd", align: "right" },
        // Total Programado
        { text: "TM", value: "TotalProgrammedTons", align: "right" },
        { text: "FCL", value: "TotalProgrammedFcl", align: "right" },
        { text: "FOB USD", value: "TotalProgrammedFobUsd", align: "right" },
        // Variación
        { text: "TM", value: "VarianceTons", align: "center" },
        { text: "FCL", value: "VarianceFcl", align: "center" },
        { text: "FOB USD", value: "VarianceFobUsd", align: "right" },
        // Líneas
        { text: "Líneas", value: "actions", align: "center", sortable: false },
      ],
    };
  },
  computed: {
    filteredList() {
      let list = this.trackingList || [];

      // Filtro de situación
      if (this.filterStatus === "WITH_PROG") {
        list = list.filter((item) => Number(item.TotalProgrammedTons || 0) > 0);
      } else if (this.filterStatus === "PENDING") {
        list = list.filter((item) => Number(item.VarianceTons || 0) < -0.0001);
      } else if (this.filterStatus === "COVERED") {
        list = list.filter((item) => Math.abs(Number(item.VarianceTons || 0)) <= 0.0001);
      } else if (this.filterStatus === "EXCEEDED") {
        list = list.filter((item) => Number(item.VarianceTons || 0) > 0.0001);
      } else if (this.filterStatus === "NO_CLIENT") {
        list = list.filter((item) => item.IsNoClient === 1);
      }

      // Buscador
      if (this.search && this.search.trim()) {
        const q = this.search.trim().toLowerCase();
        list = list.filter(
          (item) =>
            (item.CustomerName && item.CustomerName.toLowerCase().includes(q)) ||
            (item.PrsCustomerID && item.PrsCustomerID.toLowerCase().includes(q))
        );
      }

      return list;
    },
    totals() {
      const list = this.filteredList;
      return {
        BudgetTons: list.reduce((acc, c) => acc + Number(c.BudgetTons || 0), 0),
        BudgetFcl: list.reduce((acc, c) => acc + Number(c.BudgetFcl || 0), 0),
        BudgetFobUsd: list.reduce((acc, c) => acc + Number(c.BudgetFobUsd || 0), 0),
        PlannedTons: list.reduce((acc, c) => acc + Number(c.PlannedTons || 0), 0),
        PlannedFcl: list.reduce((acc, c) => acc + Number(c.PlannedFcl || 0), 0),
        PlannedFobUsd: list.reduce((acc, c) => acc + Number(c.PlannedFobUsd || 0), 0),
        SoldTons: list.reduce((acc, c) => acc + Number(c.SoldTons || 0), 0),
        SoldFcl: list.reduce((acc, c) => acc + Number(c.SoldFcl || 0), 0),
        SoldFobUsd: list.reduce((acc, c) => acc + Number(c.SoldFobUsd || 0), 0),
        TotalProgrammedTons: list.reduce((acc, c) => acc + Number(c.TotalProgrammedTons || 0), 0),
        TotalProgrammedFcl: list.reduce((acc, c) => acc + Number(c.TotalProgrammedFcl || 0), 0),
        TotalProgrammedFobUsd: list.reduce((acc, c) => acc + Number(c.TotalProgrammedFobUsd || 0), 0),
        VarianceTons: list.reduce((acc, c) => acc + Number(c.VarianceTons || 0), 0),
        VarianceFcl: list.reduce((acc, c) => acc + Number(c.VarianceFcl || 0), 0),
        VarianceFobUsd: list.reduce((acc, c) => acc + Number(c.VarianceFobUsd || 0), 0),
      };
    },
    budgetAdvancePercent() {
      const budget = Number(this.totals.BudgetTons || 0);
      const prog = Number(this.totals.TotalProgrammedTons || 0);
      if (budget <= 0) {
        return prog > 0 ? "100.00" : "0.00";
      }
      return ((prog / budget) * 100).toFixed(2);
    },
    lastUpdateDate() {
      if (!this.trackingList || this.trackingList.length === 0) return null;
      let maxDate = null;
      this.trackingList.forEach((item) => {
        if (item.LastUpdate) {
          const d = new Date(item.LastUpdate);
          if (!maxDate || d > maxDate) maxDate = d;
        }
      });
      return maxDate;
    },
    varianceBadgeColor() {
      const v = Number(this.totals.VarianceTons || 0);
      if (v < -0.0001) {
        return { bg: "orange lighten-5", icon: "orange darken-3", iconName: "mdi-clock-outline" };
      }
      if (v > 0.0001) {
        return { bg: "red lighten-5", icon: "red darken-3", iconName: "mdi-alert-circle-outline" };
      }
      return { bg: "green lighten-5", icon: "green darken-3", iconName: "mdi-check-circle-outline" };
    },
    varianceTextColor() {
      const v = Number(this.totals.VarianceTons || 0);
      if (v < -0.0001) return "orange--text text--darken-3";
      if (v > 0.0001) return "red--text text--darken-3";
      return "green--text text--darken-3";
    },
  },
  created() {
    if (this.embId && this.embId > 0) {
      this.loadTrackingData();
    }
  },
  watch: {
    embId(newId) {
      if (newId && newId > 0) {
        this.loadTrackingData();
      }
    },
  },
  methods: {
    loadTrackingData() {
      if (!this.embId) return;

      this.loading = true;
      CommercialMasterBudgetService.getProgrammingTracking(
        { EmbID: this.embId },
        this.$fun.getUserID()
      )
        .then((res) => {
          this.trackingList = res.data || [];
        })
        .catch((err) => {
          this.$fun.alert(
            "Error al obtener consolidado de seguimiento: " +
              (err.response?.data?.Message || err.message),
            "error"
          );
        })
        .finally(() => {
          this.loading = false;
        });
    },
    openCustomerDrawer(item) {
      this.drawer.customer = item;
      this.drawer.show = true;
    },
    getVarianceColor(val, item) {
      const num = Number(val || 0);
      if (item && (Number(item.AllocatedFromBagTons || 0) > 0 || Number(item.AllocatedFromBagFcl || 0) > 0)) {
        const opBalance = Number(item.OperatingBalanceTons !== undefined ? item.OperatingBalanceTons : (item.AvailableBaseTons - item.TotalProgrammedTons));
        if (opBalance >= -0.0001) {
          return {
            bg: "deep-purple lighten-5",
            text: "deep-purple darken-3",
            icon: "mdi-bag-personal-outline",
            label: "Desde bolsa",
          };
        }
      }
      if (num < -0.0001) {
        return {
          bg: "orange lighten-5",
          text: "orange darken-3",
          icon: "mdi-clock-outline",
          label: "Pendiente",
        };
      }
      if (num > 0.0001) {
        return {
          bg: "red lighten-5",
          text: "red darken-3",
          icon: "mdi-alert-circle-outline",
          label: "Exceso",
        };
      }
      return {
        bg: "green lighten-5",
        text: "green darken-4",
        icon: "mdi-check-circle-outline",
        label: "Cubierto",
      };
    },
    getVarianceTextColor(val, item) {
      const num = Number(val || 0);
      if (item && (Number(item.AllocatedFromBagTons || 0) > 0 || Number(item.AllocatedFromBagFcl || 0) > 0)) {
        const opBalance = Number(item.OperatingBalanceTons !== undefined ? item.OperatingBalanceTons : (item.AvailableBaseTons - item.TotalProgrammedTons));
        if (opBalance >= -0.0001) return "deep-purple--text text--darken-3";
      }
      if (num < -0.0001) return "orange--text text--darken-3";
      if (num > 0.0001) return "red--text text--darken-3";
      return "green--text text--darken-3";
    },
    exportToExcel() {
      try {
        const rows = this.filteredList.map((item, idx) => ({
          "#": idx + 1,
          "Código SAP": item.PrsCustomerID || "",
          "Cliente Comercial": item.CustomerName || "Sin Cliente",
          "Tipo": item.IsNoClient ? "Sin Cliente" : "Cliente",
          "Ppto TM": Number(item.BudgetTons || 0),
          "Ppto FCL": Number(item.BudgetFcl || 0),
          "Ppto FOB USD": Number(item.BudgetFobUsd || 0),
          "Plan TM": Number(item.PlannedTons || 0),
          "Plan FCL": Number(item.PlannedFcl || 0),
          "Plan FOB USD": Number(item.PlannedFobUsd || 0),
          "Vendido TM": Number(item.SoldTons || 0),
          "Vendido FCL": Number(item.SoldFcl || 0),
          "Vendido FOB USD": Number(item.SoldFobUsd || 0),
          "Total Prog TM": Number(item.TotalProgrammedTons || 0),
          "Total Prog FCL": Number(item.TotalProgrammedFcl || 0),
          "Total Prog FOB USD": Number(item.TotalProgrammedFobUsd || 0),
          "Variación TM": Number(item.VarianceTons || 0),
          "Variación FCL": Number(item.VarianceFcl || 0),
          "Variación FOB USD": Number(item.VarianceFobUsd || 0),
          "Total Líneas": item.TotalLines || 0,
        }));

        // Fila de totales
        rows.push({
          "#": "",
          "Código SAP": "TOTALES",
          "Cliente Comercial": `TOTAL GENERAL (${this.filteredList.length} Clientes)`,
          "Tipo": "",
          "Ppto TM": this.totals.BudgetTons,
          "Ppto FCL": this.totals.BudgetFcl,
          "Ppto FOB USD": this.totals.BudgetFobUsd,
          "Plan TM": this.totals.PlannedTons,
          "Plan FCL": this.totals.PlannedFcl,
          "Plan FOB USD": this.totals.PlannedFobUsd,
          "Vendido TM": this.totals.SoldTons,
          "Vendido FCL": this.totals.SoldFcl,
          "Vendido FOB USD": this.totals.SoldFobUsd,
          "Total Prog TM": this.totals.TotalProgrammedTons,
          "Total Prog FCL": this.totals.TotalProgrammedFcl,
          "Total Prog FOB USD": this.totals.TotalProgrammedFobUsd,
          "Variación TM": this.totals.VarianceTons,
          "Variación FCL": this.totals.VarianceFcl,
          "Variación FOB USD": this.totals.VarianceFobUsd,
          "Total Líneas": "",
        });

        // Convertir a CSV estándar
        const headers = Object.keys(rows[0]);
        const csvContent =
          "data:text/csv;charset=utf-8,\uFEFF" +
          [
            headers.join(";"),
            ...rows.map((row) =>
              headers
                .map((field) => {
                  const val = row[field] === null || row[field] === undefined ? "" : String(row[field]);
                  return `"${val.replace(/"/g, '""')}"`;
                })
                .join(";")
            ),
          ].join("\r\n");

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute(
          "download",
          `Seguimiento_Programacion_Ppto_${this.budgetData.EmbName || this.embId}_${new Date().toISOString().slice(0, 10)}.csv`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        this.$fun.alert("Reporte exportado correctamente.", "success");
      } catch (err) {
        this.$fun.alert("Error al exportar reporte: " + err.message, "error");
      }
    },
    formatDateTime(d) {
      if (!d) return "-";
      return new Date(d).toLocaleString();
    },
  },
  filters: {
    formatNumber(val) {
      if (val === null || val === undefined || isNaN(val)) return "0.00";
      return Number(val).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatFcl(val) {
      if (val === null || val === undefined || isNaN(val)) return "0.00";
      return Number(val).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatMoney(val) {
      if (val === null || val === undefined || isNaN(val)) return "0.00";
      return Number(val).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatPrice(val) {
      if (val === null || val === undefined || isNaN(val)) return "0.0000";
      return Number(val).toLocaleString(undefined, {
        minimumFractionDigits: 4,
        maximumFractionDigits: 4,
      });
    },
  },
};
</script>

<style scoped>
.hover-primary:hover {
  color: #1976d2 !important;
  text-decoration: underline;
}
.cursor-pointer {
  cursor: pointer;
}
.table-container {
  overflow-x: auto;
  position: relative;
}
.tracking-table {
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;
}
.tracking-table ::v-deep .sticky-col {
  position: sticky !important;
  left: 0 !important;
  z-index: 2 !important;
  background-color: #ffffff !important;
}
.tracking-table ::v-deep thead .sticky-col {
  z-index: 4 !important;
  background-color: #f8fafc !important;
}
.tracking-table ::v-deep tr.totals-row .sticky-col {
  z-index: 3 !important;
  background-color: #f1f5f9 !important;
}
.tracking-table ::v-deep .border-right {
  border-right: 1px solid #e2e8f0 !important;
}
.tracking-table ::v-deep .super-header-client {
  vertical-align: middle !important;
  background-color: #f8fafc !important;
  color: #0f172a !important;
  font-size: 0.75rem !important;
  font-weight: 800 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #cbd5e1 !important;
  padding: 8px 12px !important;
}
.tracking-table ::v-deep .super-header-group {
  background-color: #f1f5f9 !important;
  color: #0f172a !important;
  font-size: 0.725rem !important;
  font-weight: 800 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #cbd5e1 !important;
  padding: 6px 8px !important;
}
.tracking-table ::v-deep .super-header-action {
  vertical-align: middle !important;
  background-color: #f8fafc !important;
  color: #0f172a !important;
  font-size: 0.75rem !important;
  font-weight: 800 !important;
  text-transform: uppercase !important;
  border-bottom: 1px solid #cbd5e1 !important;
  padding: 8px 12px !important;
}
.tracking-table ::v-deep tr.sub-headers th {
  background-color: #f8fafc !important;
  color: #334155 !important;
  font-size: 0.7rem !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  border-bottom: 1px solid #cbd5e1 !important;
  padding: 6px 8px !important;
}
.tracking-table ::v-deep td {
  font-size: 0.8rem !important;
  border-bottom: 1px solid #f1f5f9 !important;
  padding: 6px 8px !important;
  white-space: nowrap;
}
.totals-row td {
  background-color: #f1f5f9 !important;
  border-top: 2px solid #cbd5e1 !important;
  font-size: 0.825rem !important;
}
</style>
